import type { JSX } from "react";

import Box from "@/ui/layout/Box";
import type { FlatListProps } from "@/ui/layout/list/FlatList";
import FlatList from "@/ui/layout/list/FlatList";
import { HStack, VStack } from "@/ui/layout/Stacks";
import { ColorToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

const TwoColumnRow = ({
  leftItem,
  rightItem,
}: {
  leftItem?: JSX.Element | null;
  rightItem?: JSX.Element | null;
}) => {
  return (
    <HStack justifyContent="flex-start" paddingHorizontal={24}>
      {leftItem}
      {rightItem}
    </HStack>
  );
};

interface TwoColumnListProps<T> extends BaseViewProps {
  data: T[];
  emptyListComponent?: () => JSX.Element;
  header?: JSX.Element;
  headerLeftItem: JSX.Element;
  headerRightItem: JSX.Element;
  // eslint-disable-next-line max-params
  keyExtractor: (item: T, index: number) => string;
  onEndReached?: FlatListProps<T>["onEndReached"];
  onEndReachedThreshold?: FlatListProps<T>["onEndReachedThreshold"];
  renderLeftItem: ({ item }: { item: T }) => JSX.Element;
  renderRightItem: ({ item }: { item: T }) => JSX.Element;
}

const TwoColumnList = <T,>({
  data,
  emptyListComponent,
  header,
  headerLeftItem,
  headerRightItem,
  keyExtractor,
  onEndReached,
  onEndReachedThreshold,
  renderLeftItem,
  renderRightItem,
  testID,
}: TwoColumnListProps<T>) => {
  const baseTestID = joinTestIDs(testID, "two-column-list");
  return (
    <FlatList
      data={data}
      emptyListComponent={emptyListComponent}
      keyExtractor={keyExtractor}
      ListHeaderComponent={
        <VStack>
          {header}
          <Box
            backgroundColor={ColorToken.NeutralBackgroundSecondary}
            paddingVertical={11}
          >
            <TwoColumnRow
              leftItem={headerLeftItem}
              rightItem={headerRightItem}
            />
          </Box>
        </VStack>
      }
      onEndReached={onEndReached}
      onEndReachedThreshold={onEndReachedThreshold}
      renderItem={({ item }) => {
        return (
          <Box paddingVertical={13.5}>
            <TwoColumnRow
              leftItem={renderLeftItem({ item })}
              rightItem={renderRightItem({ item })}
            />
          </Box>
        );
      }}
      separatorType="line"
      testID={baseTestID}
    />
  );
};

export type { TwoColumnListProps };
export default TwoColumnList;
